/**
 * ทะลวงขั้น (breakthrough gate, docs/design/2026-10-11-gameplay-loop.md Fix B): stage 9 of a realm holds until a
 * ยาทะลวงขั้น is used and the guardian's trial is won (systems/breakthrough.ts). One pill per realm step, brewed by the
 * herbalist from six materials of that tier's beasts and one boss material. Data only.
 */
import type { DropEntry } from './drops';
import type { GoodDef, RecipeDef } from './professions';

export const BREAKTHROUGH = {
  /** Seconds before the trial may be tried again after a loss (10 min). */
  retrySeconds: 600,
  /** Tier materials in a pill. */
  tierMats: 6,
} as const;

interface Step {
  readonly name: string;
  /** Monster ids (data/monsters.ts) that drop the tier material. */
  readonly beasts: readonly string[];
  /** The monster that drops the boss material. */
  readonly boss: string;
  readonly mat: string;
  readonly bossMat: string;
}

/** Step k = the pill that lifts the hold at stage 9 of realm k (REALMS index). Realm 7 (k = 6) waits for the realms to come. */
const STEPS: readonly Step[] = [
  { name: 'ฝึกกาย', beasts: ['wolf', 'boar'], boss: 'masked-chief', mat: 'เขี้ยวสัตว์ป่า', bossMat: 'ตราหัวหน้าโจร' },
  { name: 'ฝึกลมปราณ', beasts: ['bandit', 'masked'], boss: 'boss', mat: 'ผงลมปราณโจร', bossMat: 'แก่นหัวหน้าด่าน' },
  { name: 'ยอดฝีมือชั้นสาม', beasts: ['viper', 'scorpion', 'monk'], boss: 'wb-ape', mat: 'พิษเข้มข้น', bossMat: 'ขนวานรยักษ์' },
  { name: 'ยอดฝีมือชั้นสอง', beasts: ['leopard', 'bloodbat', 'knifer'], boss: 'wb-firebird', mat: 'กรงเล็บคม', bossMat: 'ขนวิหคเพลิง' },
  { name: 'ยอดฝีมือชั้นหนึ่ง', beasts: ['spider', 'centipede', 'jiangshi'], boss: 'whitetiger', mat: 'ไหมพิษ', bossMat: 'เขี้ยวพยัคฆ์ขาว' },
  { name: 'ยอดฝีมือไร้เทียมทาน', beasts: ['python', 'foxspirit', 'frostman'], boss: 'worldboss', mat: 'เกล็ดวิญญาณ', bossMat: 'หัวใจสัตว์เทพ' },
  { name: 'ปรมาจารย์', beasts: ['assassin', 'snowwolf'], boss: 'worldboss', mat: 'แก่นปราณลึก', bossMat: 'แก่นสัตว์เทพ' },
];

export const BREAKTHROUGH_STEPS = STEPS.length;
export const pillId = (k: number): string => `bt-pill-${k}`;
export const tierMatId = (k: number): string => `bt-mat-${k}`;
export const bossMatId = (k: number): string => `bt-boss-${k}`;
/** The pill id of the hold at stage 9 of realm `k`, or null. */
export const pillOf = (k: number): string | null => (k >= 0 && k < STEPS.length ? pillId(k) : null);

export const BREAKTHROUGH_GOODS: readonly GoodDef[] = STEPS.flatMap((s, k): GoodDef[] => [
  { id: tierMatId(k), name: s.mat, kind: 'material', desc: `วัตถุดิบจากสัตว์ร้ายขั้น${s.name} ใช้ทำยาทะลวงขั้น`, price: 20 + k * 15 },
  { id: bossMatId(k), name: s.bossMat, kind: 'material', desc: `ของจากหัวหน้าขั้น${s.name} ใช้ทำยาทะลวงขั้น`, price: 120 + k * 80 },
  { id: pillId(k), name: `ยาทะลวงขั้น${s.name}`, kind: 'medicine', desc: `ใช้เมื่อค้างที่ชั้น 9 ของขั้น${s.name} เพื่อเปิดด่านทดสอบ`, price: 300 + k * 200 },
]);

export const BREAKTHROUGH_RECIPES: readonly RecipeDef[] = STEPS.map((_, k) => ({
  id: pillId(k),
  prof: 'herbalist' as const,
  level: Math.min(10, 3 + k),
  inputs: [
    { id: tierMatId(k), n: BREAKTHROUGH.tierMats },
    { id: bossMatId(k), n: 1 },
  ],
  out: { good: pillId(k), n: 1 },
  time: 10,
  xp: 40 + k * 20,
}));

/** Drop rows by monster id: tier materials from the beasts, the boss material from the boss (folded in by systems/items.ts). */
export const BREAKTHROUGH_DROPS: Readonly<Record<string, readonly DropEntry[]>> = (() => {
  const o: Record<string, DropEntry[]> = {};
  STEPS.forEach((s, k) => {
    for (const b of s.beasts) (o[b] ??= []).push({ good: tierMatId(k), chance: 0.25, n: [1, 2] });
    (o[s.boss] ??= []).push({ good: bossMatId(k), chance: 0.5, n: [1, 1] });
  });
  return o;
})();

/** Saved with the character (`PlayerProgress.breakthrough`, absent until the first hold). */
export interface BreakthroughState {
  /** Realms passed (the hold at stage 9 of realm `passed` is the one in force). */
  passed: number;
  /** Cultivation earned while held, paid back on the win. */
  bank: number;
  /** Seconds left before the trial may be tried again. */
  cool: number;
  /** The pill is used: the trial may be fought. */
  open: boolean;
}

export const newBreakthrough = (): BreakthroughState => ({ passed: 0, bank: 0, cool: 0, open: false });

const nn = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);

export function parseBreakthrough(v: unknown): BreakthroughState {
  const r = (v && typeof v === 'object' ? v : {}) as Partial<BreakthroughState>;
  return { passed: Math.floor(nn(r.passed)), bank: Math.floor(nn(r.bank)), cool: nn(r.cool), open: r.open === true };
}
